import type { HintMarker, PurchaseHint, ShoppingListItem, Store } from '@recipes/contracts';
import { Button } from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { ChevronDown, MessageSquarePlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { offlineDb } from '@/lib/offline/db';
import { queryKeys } from '@/lib/query-keys';
import { resolveHints } from '@recipes/domain';
import { HintMarkerBadge, NutriScoreBadge } from './badges';
import { HintDialog, HintList } from './hint-dialog';

export interface ListItemHints {
  marker: HintMarker;
  applicable: PurchaseHint[];
  elsewhere: PurchaseHint[];
  ingredientId: string | null;
  ingredientName: string | null;
}

/**
 * Hints for every item of a list (docs/finished/ingredient-catalog-spec.md 5.5). The device copy is
 * the base – it works offline and reacts at once to hints written on this device. Online, one
 * request adds what only the server knows: links resolved for this reader.
 */
export function useListHints(
  listId: string,
  items: ShoppingListItem[],
  store: Store | null,
): Map<string, ListItemHints> {
  const online = useOnlineStatus();
  const remote = useQuery({
    queryKey: queryKeys.catalog.listHints(listId, store?.id ?? null),
    queryFn: () => api.catalog.shoppingListHints(listId, store?.id),
    enabled: online && Boolean(listId) && items.length > 0,
    staleTime: 30_000,
    retry: false,
  });
  const hints = useLiveQuery(() => offlineDb.catalogHints.toArray(), []);
  const ingredients = useLiveQuery(() => offlineDb.catalogIngredients.toArray(), []);

  return useMemo(() => {
    const out = new Map<string, ListItemHints>();
    const all = (hints ?? []).map((r) => r.hint);
    const names = new Map((ingredients ?? []).map((r) => [r.id, r.ingredient.name]));
    const resolvedRemotely = new Map(
      (remote.data?.items ?? []).map((entry) => [entry.itemId, entry.ingredient]),
    );
    for (const item of items) {
      const ingredientId = resolvedRemotely.get(item.id)?.id ?? item.ingredientId ?? null;
      const productId = item.product?.id ?? null;
      if (!ingredientId && !productId) continue;
      const resolved = resolveHints(all, {
        ingredientId,
        productId,
        storeId: store?.id ?? null,
        storeParentId: store?.parentStoreId ?? null,
      });
      out.set(item.id, {
        ...resolved,
        ingredientId,
        ingredientName:
          resolvedRemotely.get(item.id)?.name ??
          (ingredientId ? (names.get(ingredientId) ?? null) : null),
      });
    }
    return out;
  }, [hints, ingredients, items, remote.data, store]);
}

/**
 * Marker, Nutri-Score, hint count and the "add hint" button on the item's line, plus the expanded
 * hints as a full-width line below it. Rendered inside the row's wrapping flex container, so the
 * pieces are flex items; the product name is part of the row's own text.
 */
export function ItemHintDetails({
  item,
  hints,
  store,
}: {
  item: ShoppingListItem;
  hints: ListItemHints | undefined;
  store: Store | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(false);
  const count = (hints?.applicable.length ?? 0) + (hints?.elsewhere.length ?? 0);
  const target = item.product
    ? { productId: item.product.id, name: item.product.name }
    : hints?.ingredientId
      ? { ingredientId: hints.ingredientId, name: hints.ingredientName ?? item.name }
      : null;
  if (!item.product && !target) return null;

  return (
    <>
      {hints && <HintMarkerBadge marker={hints.marker} className="shrink-0" />}
      {item.product?.nutriScore && <NutriScoreBadge grade={item.product.nutriScore} size="sm" />}
      {count > 0 && (
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 px-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="item-hints-toggle"
        >
          {t('catalog.hint.count', { count })}
          <ChevronDown
            className={`size-3 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      )}
      {target && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-muted-foreground"
          onClick={() => setDialog(true)}
          aria-label={t('catalog.hint.add')}
          title={t('catalog.hint.add')}
          data-testid="item-add-hint"
        >
          <MessageSquarePlus className="size-4" aria-hidden />
        </Button>
      )}
      {open && hints && target && (
        <div className="order-last basis-full space-y-2 pb-2 pl-12 pr-2" data-testid="item-hints">
          <HintList hints={hints.applicable} target={target} />
          {hints.elsewhere.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">
                {t('catalog.hint.elsewhereTitle')}
              </p>
              <HintList hints={hints.elsewhere} target={target} />
            </div>
          )}
        </div>
      )}
      {target && (
        <HintDialog
          open={dialog}
          onOpenChange={setDialog}
          target={target}
          defaultStoreId={store?.id ?? null}
        />
      )}
    </>
  );
}

/** True when the household recommends another store for this item and nothing speaks for this one. */
export function boughtElsewhere(hints: ListItemHints | undefined): boolean {
  if (!hints) return false;
  if (hints.marker === 'AVOID') return true;
  return hints.marker !== 'RECOMMENDED' && hints.elsewhere.some((h) => h.verdict === 'RECOMMENDED');
}
